import { BookOpenText } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Textarea } from '@/components/ui/Textarea'
import { useSaveMaterial } from '../api'
import type { Material, MaterialKind } from '../types'
import { AttachmentField, type Attached } from './AttachmentField'
import { KIND_META } from '../kinds'
import { ToggleRow } from './ToggleRow'
import { useFieldErrors } from './useFieldErrors'
import { VideoUploadField } from './VideoUploadField'

const FIELDS = ['kind', 'title', 'unit', 'body', 'url', 'fileIds', 'videoId'] as const

/** Add or edit a lesson, resource, link or video; save as draft or publish to students. */
export function MaterialDialog({
  sectionId,
  subjectId,
  material,
  units,
  onOpenChange,
}: {
  sectionId: number
  subjectId: number
  material?: Material
  units: string[]
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('lms')
  const save = useSaveMaterial(sectionId, subjectId)
  const unitList = useId()
  const [kind, setKind] = useState<MaterialKind>(material?.kind ?? 'lesson')
  const [title, setTitle] = useState(material?.title ?? '')
  const [unit, setUnit] = useState(material?.unit ?? units.at(-1) ?? '')
  const [body, setBody] = useState(material?.body ?? '')
  const [url, setUrl] = useState(material?.url ?? '')
  const [files, setFiles] = useState<Attached[]>(
    material?.files.map((f) => ({ id: f.id, name: f.name, size: f.size })) ?? [],
  )
  const [published, setPublished] = useState(material?.published ?? true)
  const [videoSource, setVideoSource] = useState<'link' | 'upload'>(material?.video ? 'upload' : 'link')
  const [videoId, setVideoId] = useState<number | null>(material?.video?.id ?? null)
  const [videoBusy, setVideoBusy] = useState(false)
  const { errors, formError, reset, apply } = useFieldErrors(FIELDS)
  const uploadsVideo = kind === 'video' && videoSource === 'upload'
  const needsUrl = kind === 'link' || (kind === 'video' && videoSource === 'link')

  const submit = async () => {
    reset()
    try {
      await save.mutateAsync({
        id: material?.id,
        kind,
        title,
        unit: unit || null,
        body: body || null,
        url: needsUrl ? url || null : null,
        videoId: uploadsVideo ? videoId : null,
        published,
        fileIds: files.map((f) => f.id),
      })
      toast.success(published ? t('Published for students') : t('Saved as a draft'))
      onOpenChange(false)
    } catch (e) {
      apply(e)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      icon={<BookOpenText />}
      title={material ? t('Edit content') : t('Add content')}
      description={t('Students see it once it is published.')}
      dismissible={!save.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={save.isPending} disabled={videoBusy}>
            {published ? t('Publish') : t('Save draft')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {formError ? (
          <Alert tone="danger" title={t('The content was not saved')}>
            {formError}
          </Alert>
        ) : null}
        <FormField label={t('Type')} error={errors.kind}>
          <RadioGroup
            appearance="cards"
            className="sm:grid-cols-4"
            value={kind}
            onValueChange={(v) => setKind(v as MaterialKind)}
            options={(Object.keys(KIND_META) as MaterialKind[]).map((k) => ({
              value: k,
              label: t(KIND_META[k].label),
            }))}
          />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label={t('Title')} required error={errors.title} className="sm:col-span-2">
            <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          </FormField>
          <FormField label={t('Unit')} error={errors.unit} hint={t('e.g. Unit 1, Chapter 3')}>
            <Input value={unit} maxLength={80} list={unitList} onChange={(e) => setUnit(e.target.value)} />
          </FormField>
          {/* Outside FormField, which passes its props to a single control. */}
          <datalist id={unitList}>
            {units.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </div>
        {kind === 'video' ? (
          <FormField label={t('Video source')}>
            <RadioGroup
              appearance="cards"
              value={videoSource}
              onValueChange={(v) => setVideoSource(v as 'link' | 'upload')}
              options={[
                {
                  value: 'upload',
                  label: t('Upload a video'),
                  description: t('Streamed to students, adapting to their connection.'),
                },
                {
                  value: 'link',
                  label: t('Video link'),
                  description: t('YouTube, Vimeo or any web address.'),
                },
              ]}
            />
          </FormField>
        ) : null}
        {uploadsVideo ? (
          <FormField label={t('Video')} required error={errors.videoId}>
            <VideoUploadField
              initial={material?.video ?? null}
              onChange={setVideoId}
              onBusyChange={setVideoBusy}
              error={errors.url}
            />
          </FormField>
        ) : null}
        {needsUrl ? (
          <FormField
            label={kind === 'video' ? t('Video address') : t('Web address')}
            required
            error={errors.url}
          >
            <Input type="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
          </FormField>
        ) : null}
        <FormField
          label={kind === 'lesson' ? t('Lesson') : t('Description')}
          required={kind === 'lesson' && files.length === 0}
          error={errors.body}
          hint={t('Links written in the text become clickable.')}
        >
          <Textarea rows={kind === 'lesson' ? 8 : 3} value={body} onChange={(e) => setBody(e.target.value)} />
        </FormField>
        <FormField label={t('Files')} required={kind === 'resource'} error={errors.fileIds}>
          <AttachmentField value={files} onChange={setFiles} disabled={save.isPending} />
        </FormField>
        <ToggleRow
          label={t('Published')}
          hint={t('Off: only teachers of the course can see it.')}
          checked={published}
          onCheckedChange={setPublished}
        />
      </div>
    </Dialog>
  )
}
